import React from 'react';
import { ChevronLeftIcon, ListBulletIcon } from '@heroicons/react/24/outline';

/**
 * BackControlsDual - Dual navigation buttons for Results interface
 *
 * Provides side-by-side "Parent" and "All/Back" navigation controls per canvas design.
 * Fully accessible with keyboard support, ARIA labels, and focus management.
 *
 * @param parentTitle - Optional title of parent comparison for enhanced accessibility
 * @param onNavigateToParent - Handler for Parent button click/Enter/Space
 * @param onNavigateToAll - Handler for All/Back button click/Enter/Space (navigates to all comparisons list)
 * @param showParentButton - Whether to show Parent button (default: true). Use false for Comparison detail view.
 * @param useAllButton - Whether to show "All" button (true) or "Back" button (false). Default: false.
 *
 * Prop flow:
 * - parentTitle flows to aria-label AND title tooltip for screen readers
 * - Handlers are optional; buttons render even if undefined (no-op)
 * - Empty/undefined parentTitle triggers fallback text
 * - showParentButton controls Parent button visibility (Responses show both, Comparisons show only All/Back)
 * - useAllButton controls button style: true = "All" (list icon), false = "Back" (arrow icon)
 *
 * Usage:
 * - Response detail: showParentButton={true}, useAllButton={true} → Shows [Parent] [All]
 * - Comparison detail: showParentButton={false}, useAllButton={false} → Shows [Back]
 */
interface BackControlsDualProps {
  parentTitle?: string;
  onNavigateToParent?: () => void;
  onNavigateToAll?: () => void;
  showParentButton?: boolean;
  useAllButton?: boolean;
}

export const BackControlsDual: React.FC<BackControlsDualProps> = ({
  parentTitle,
  onNavigateToParent,
  onNavigateToAll,
  showParentButton = true,
  useAllButton = false
}) => {
  const handleParentKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && onNavigateToParent) {
      e.preventDefault();
      onNavigateToParent();
    }
  };

  const handleAllKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && onNavigateToAll) {
      e.preventDefault();
      onNavigateToAll();
    }
  };

  const parentAriaLabel = parentTitle
    ? `Navigate back to parent comparison: ${parentTitle}`
    : 'Navigate back to parent comparison';

  return (
    <nav className="flex items-center gap-2" aria-label="Comparison navigation">
      {/* Parent Button - conditionally rendered */}
      {showParentButton && (
        <button
          type="button"
          onClick={onNavigateToParent}
          onKeyDown={handleParentKeyDown}
          className="inline-flex items-center gap-2 px-3 py-2 min-h-[44px] rounded-2xl bg-slate-800/50 border border-slate-700/50 text-slate-300 hover:bg-slate-700/50 hover:border-cyan-500/30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900"
          aria-label={parentAriaLabel}
          title={parentTitle || 'Navigate to parent comparison'}
          tabIndex={0}
        >
          <ChevronLeftIcon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
        </button>
      )}

      {/* All/Back Button - always rendered */}
      <button
        type="button"
        onClick={onNavigateToAll}
        onKeyDown={handleAllKeyDown}
        className="inline-flex items-center gap-2 px-3 py-2 min-h-[44px] rounded-2xl bg-slate-800/50 border border-slate-700/50 text-slate-300 hover:bg-slate-700/50 hover:border-cyan-500/30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900"
        aria-label={useAllButton ? "Navigate back to all comparisons list" : "Back to comparisons list"}
        title={useAllButton ? "Navigate to all comparisons" : "Back to all comparisons"}
        tabIndex={0}
      >
        {useAllButton ? (
          <ListBulletIcon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
        ) : (
          <ChevronLeftIcon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
        )}
        {useAllButton && <span className="text-sm font-medium truncate">All</span>}
      </button>
    </nav>
  );
};
